import { useEffect, useState } from 'react';
import {
  DEFAULT_COMPANY_DETAILS,
  LEGAL_DOCUMENTS,
  LEGAL_DOCUMENT_KEYS,
  NEW_LIFE_WAIT_DAYS,
  type AccountView,
  type PlayerSettings,
} from '@london-life/contracts';
import { describeError, type FriendlyError } from '@london-life/errors';
import { Button } from '@london-life/ui/components/button';
import { Switch } from '@london-life/ui/components/switch';
import { ErrorNotice } from '@london-life/ui/feedback/error-notice';
import { DeleteAccountForm } from '@london-life/ui/auth/profile-cards';
import { api } from '../lib/api';
import { pushState, turnPushOff, turnPushOn, type PushState } from '../lib/push';

const PUSH_HINT: Record<PushState, string> = {
  on: 'Your phone shows new messages when the game is closed.',
  off: 'Get new messages on this phone or computer even when the game is closed.',
  blocked: 'Alerts are blocked for this site. Allow notifications for londonlifegame.com in your browser settings.',
  install: 'On iPhone: tap Share, then Add to Home Screen, and open London Life from there. Then switch this on.',
  unsupported: 'This browser cannot show alerts when the game is closed.',
};

// the short guide to playing, kept here so it is always one tap away
const GUIDE = [
  [
    'Get around',
    'Tap a place on the map, pick how to travel (walking is free; the bus, DLR, Tube and cabs cost real fares), then Go.',
  ],
  [
    'Earn',
    'Work shifts at the office, café, market or Billingsgate. You can earn up to £300 a day; tired people cannot work.',
  ],
  ['Look after yourself', 'Your six needs fall with time. Eat, sleep, shower, have fun, see people, and use the loo.'],
  [
    'Feelings and mood',
    'Your needs and what happens to you make feelings, each worth mood points. Check the Needs tab.',
  ],
  [
    'Your money',
    'Everything is in pounds and pence and every penny is accounted for. Nobody can take money from your wallet.',
  ],
] as const;

export function SettingsTab({
  account,
  settings,
  onSettings,
  onSignOut,
  onDeleteAccount,
  onCookieSettings,
  onNewLife,
}: {
  account: AccountView;
  settings: PlayerSettings;
  onSettings: (settings: PlayerSettings) => void;
  onSignOut: () => void;
  onDeleteAccount: (confirmUsername: string) => Promise<void>;
  onCookieSettings: () => void;
  onNewLife: () => Promise<void>;
}) {
  const [failure, setFailure] = useState<FriendlyError | null>(null);
  const [support, setSupport] = useState<string>(DEFAULT_COMPANY_DETAILS.supportEmail);
  const [deleting, setDeleting] = useState(false);
  const [endingLife, setEndingLife] = useState(false);
  const [busy, setBusy] = useState(false);
  const [push, setPush] = useState<PushState | null>(null);
  useEffect(() => {
    void pushState()
      .then(setPush)
      .catch(() => setPush('unsupported'));
  }, []);
  const switchPush = async (on: boolean) => {
    setFailure(null);
    try {
      setPush(await (on ? turnPushOn() : turnPushOff()));
    } catch (error) {
      setFailure(describeError(error));
    }
  };
  useEffect(() => {
    api
      .companyDetails()
      .then((reply) => setSupport(reply.company.supportEmail))
      .catch(() => {});
  }, []);
  const change = async (next: PlayerSettings) => {
    setFailure(null);
    const before = settings;
    onSettings(next);
    try {
      onSettings({ ...next, ...(await api.account.saveSettings(next)).settings });
    } catch (error) {
      onSettings(before);
      setFailure(describeError(error));
    }
  };
  const newLife = async () => {
    setBusy(true);
    setFailure(null);
    try {
      await onNewLife();
    } catch (error) {
      setFailure(describeError(error));
      setBusy(false);
    }
  };
  return (
    <div className="profile-grid">
      {failure && <ErrorNotice error={failure} />}
      <section className="profile-card" aria-labelledby="settings-graphics">
        <h3 id="settings-graphics">Graphics</h3>
        <label className="settings-row">
          <span>
            <strong>Data saver</strong>
            <small>Lighter graphics for older phones and mobile data.</small>
          </span>
          <Switch
            aria-label="Data saver"
            checked={settings.dataSaver}
            onCheckedChange={(dataSaver) => void change({ ...settings, dataSaver })}
          />
        </label>
      </section>

      <section className="profile-card" aria-labelledby="settings-chat">
        <h3 id="settings-chat">Chat</h3>
        <label className="settings-row">
          <span>
            <strong>Read receipts</strong>
            <small>People you message see when you have read theirs, and you see theirs.</small>
          </span>
          <Switch
            aria-label="Read receipts"
            checked={settings.readReceipts}
            onCheckedChange={(readReceipts) => void change({ ...settings, readReceipts })}
          />
        </label>
      </section>

      <section className="profile-card" aria-labelledby="settings-alerts">
        <h3 id="settings-alerts">Alerts</h3>
        <label className="settings-row">
          <span>
            <strong>Sounds</strong>
            <small>A short chime when a message pops up.</small>
          </span>
          <Switch
            aria-label="Sounds"
            checked={settings.sounds}
            onCheckedChange={(sounds) => void change({ ...settings, sounds })}
          />
        </label>
        <label className="settings-row">
          <span>
            <strong>Alerts when the game is closed</strong>
            <small>{push ? PUSH_HINT[push] : 'Checking this browser…'}</small>
          </span>
          <Switch
            aria-label="Alerts when the game is closed"
            checked={push === 'on'}
            disabled={!push || push === 'unsupported' || push === 'install' || push === 'blocked'}
            onCheckedChange={(on) => void switchPush(on)}
          />
        </label>
      </section>

      <section className="profile-card" aria-labelledby="settings-help">
        <h3 id="settings-help">Help</h3>
        {GUIDE.map(([title, text]) => (
          <details key={title} className="settings-guide">
            <summary>{title}</summary>
            <p>{text}</p>
          </details>
        ))}
        <p className="profile-hint">
          Stuck, or something wrong? Email <a href={`mailto:${support}`}>{support}</a>. We never ask for your password.
        </p>
      </section>

      <section className="profile-card" aria-labelledby="settings-life">
        <h3 id="settings-life">Your life</h3>
        {!endingLife ? (
          <>
            <p className="profile-hint">
              Start again as someone new. Your current character ends for good. You can start a new life once every{' '}
              {NEW_LIFE_WAIT_DAYS} days.
            </p>
            <Button type="button" variant="outline" onClick={() => setEndingLife(true)}>
              Start a new life
            </Button>
          </>
        ) : (
          <div className="settings-confirm" role="group" aria-label="Start a new life">
            <p>
              <strong>End this life?</strong> Your character, home and money in this life end and cannot be brought
              back.
            </p>
            <div className="profile-actions">
              <Button type="button" variant="outline" onClick={() => setEndingLife(false)} disabled={busy}>
                Keep playing
              </Button>
              <Button type="button" variant="destructive" onClick={() => void newLife()} disabled={busy}>
                {busy ? 'Ending…' : 'End this life'}
              </Button>
            </div>
          </div>
        )}
      </section>

      <section className="profile-card" aria-labelledby="settings-account">
        <h3 id="settings-account">Account and privacy</h3>
        <p className="profile-hint">Signed in as {account.email ?? `@${account.username}`}.</p>
        {!deleting ? (
          <div className="profile-actions wrap">
            <Button type="button" variant="outline" onClick={onSignOut}>
              Sign out
            </Button>
            <Button type="button" variant="outline" onClick={onCookieSettings}>
              Cookie settings
            </Button>
            <Button type="button" variant="ghost" className="danger-text" onClick={() => setDeleting(true)}>
              Delete my account
            </Button>
          </div>
        ) : (
          <DeleteAccountForm
            username={account.username}
            onDelete={onDeleteAccount}
            onCancel={() => setDeleting(false)}
          />
        )}
        <nav className="settings-legal" aria-label="Legal">
          {LEGAL_DOCUMENT_KEYS.map((key) => (
            <a key={key} href={LEGAL_DOCUMENTS[key].path}>
              {LEGAL_DOCUMENTS[key].title}
            </a>
          ))}
        </nav>
      </section>
    </div>
  );
}
